'use client';

/**
 * The one question we ask before a province or state pick lands.
 *
 * TWO PROMPTS, ONE CARD. The server decides which (`feature.variant`); this
 * component only renders it. They differ in tone, in which side is
 * recommended, and in which flag is shown, and nothing else:
 *
 *  - `wink` — QUEBEC ONLY, and it stays that way. Someone picks a place with a
 *    strong local identity and, once, we greet them: the Fleurdelisé, a little
 *    wave, and an offer to put Quebec first. Recommended side: Quebec.
 *  - `standard` — every other province and state. Not a greeting: narrowing to
 *    one region narrows the catalog, so we say so once and lead with the
 *    nationwide shelf, which has more on it. Country flag, no wave, no emoji.
 *    Recommended side: the whole country.
 *
 * THE FLAG SHOWS THE SCOPE BEING OFFERED FIRST. That is the rule that keeps
 * the two coherent: the wink leads with Quebec, so it flies Quebec's flag; the
 * standard confirmation leads with the country, so it flies the country's.
 *
 * WHAT THIS COMPONENT DOES NOT DO: commit. It reports a choice and nothing
 * else. The /region page owns the commit for every selection path, and keeping
 * it that way is what guarantees one selection produces exactly one commit and
 * one navigation — a dialog that committed for itself would be a second writer
 * racing the first.
 *
 * ONE CHOICE, EVER. Escape, the scrim, the X and both buttons can all fire in
 * the same frame (Escape while a click is in flight, a click that lands as the
 * card animates). `resolvedRef` makes the first one win, so "dismiss + accept"
 * cannot both reach the page.
 *
 * DISMISSING IS NOT DECLINING. The X, Escape and the scrim commit exactly what
 * the shopper clicked, with no stated ordering — so the escape hatch always
 * gives them the province they tapped, which matters more here than it did for
 * the wink now that every province asks.
 *
 * POSITIONING. The card is genuinely over the map rather than centred in the
 * viewport: on desktop the positioner is inset by the region panel's own width
 * (`md:right-80 lg:right-96`, mirroring `#region-list-panel`), which anchors it
 * to the map column with no measurement to drift. Under `md` the map is a
 * full-screen takeover, so the card becomes a bottom sheet — thumb-reachable,
 * and it does not cover the province the shopper just tapped.
 */

import React, { useCallback, useEffect, useId, useRef } from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { FiX } from 'react-icons/fi';
import type { RegionMapUiContent } from '@/lib/ui-content/region-map-ui-content';
import { fillRegionMapString } from '@/lib/ui-content/region-map-ui-content';
import type {
  SubregionFeature,
  SubregionPromptChoice,
} from '@/lib/hooks/use-subregion-eligibility';

export type SubregionPromptDialogProps = {
  /** The place being offered, already resolved and localized by the server. */
  feature: SubregionFeature;
  /** Localized country name for the "everything nationwide" side of the choice. */
  countryName: string;
  content: RegionMapUiContent;
  /**
   * Bottom sheet under `md`, popover over the map above it. LAYOUT only —
   * which of the two prompts this is comes from `feature.variant`, and the two
   * are independent (a wink can be a sheet, a scope confirmation can be a
   * popover).
   */
  layout: 'popover' | 'sheet';
  reducedMotion: boolean;
  /** Fires at most once, with what the shopper decided. */
  onChoose: (choice: SubregionPromptChoice) => void;
};

/** Tab-navigable descendants, in DOM order. */
const FOCUSABLE = 'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** Springy enough to feel like a greeting, short enough not to delay a tap. */
const CARD_SPRING = { type: 'spring', stiffness: 420, damping: 32, mass: 0.7 } as const;
const SHEET_SPRING = { type: 'spring', stiffness: 380, damping: 34, mass: 0.8 } as const;
const FADE = { duration: 0.18 } as const;

/** One side of the choice: what it says, and what it commits. */
type Side = { label: string; choice: SubregionPromptChoice };

type PromptCopy = {
  eyebrow: string;
  title: string;
  body: string;
  /** Rendered first, teal, and focused on open. */
  primary: Side;
  secondary: Side;
};

/**
 * Which strings and which recommendation, from the server's verdict.
 *
 * The two variants deliberately swap `primary` rather than reordering the
 * buttons by hand at the call site: the recommended side is the focused side,
 * the teal side and the first side, and deriving all three from one field is
 * what stops them drifting apart.
 */
function promptCopy(content: RegionMapUiContent, isWink: boolean): PromptCopy {
  if (isWink) {
    return {
      eyebrow: content.subregionPromptEyebrow,
      title: content.subregionPromptTitle,
      body: content.subregionPromptBody,
      primary: { label: content.subregionPromptLocalFirst, choice: 'subregion-first' },
      secondary: { label: content.subregionPromptCountryFirst, choice: 'country-first' },
    };
  }
  return {
    eyebrow: content.subregionScopeEyebrow,
    title: content.subregionScopeTitle,
    body: content.subregionScopeBody,
    // Nationwide leads, because nationwide is the bigger shelf. This is the
    // one place the standard confirmation actually argues for something.
    primary: { label: content.subregionScopeCountryFirst, choice: 'country-first' },
    secondary: { label: content.subregionScopeLocalFirst, choice: 'subregion-first' },
  };
}

export function SubregionPromptDialog({
  feature,
  countryName,
  content,
  layout,
  reducedMotion,
  onChoose,
}: SubregionPromptDialogProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);
  /** Whoever opened us — the map path or the list row. Focus goes back there. */
  const triggerRef = useRef<HTMLElement | null>(null);
  const resolvedRef = useRef(false);
  const titleId = useId();
  const bodyId = useId();

  const isSheet = layout === 'sheet';
  const isWink = feature.variant === 'wink';
  const copy = promptCopy(content, isWink);
  const place = feature.localName || feature.name;
  const vars = { name: place, country: countryName };
  const fill = (template: string): string => fillRegionMapString(template, vars);

  const choose = useCallback(
    (choice: SubregionPromptChoice) => {
      if (resolvedRef.current) return;
      resolvedRef.current = true;
      onChoose(choice);
    },
    [onChoose],
  );

  // Remember the trigger BEFORE moving focus, and hand it back on unmount. Runs
  // once: re-running on a prop change would record our own button as the
  // trigger and trap focus inside a dialog that is going away.
  useEffect(() => {
    triggerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // The recommended action, not the dismissing one: the shopper is being
    // offered something, and landing on "close" reads as a warning.
    primaryRef.current?.focus({ preventScroll: true });

    return () => {
      const trigger = triggerRef.current;
      // The map redraws around a selection, so the original node may be gone.
      if (trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
    };
  }, []);

  // Escape closes; Tab cycles inside the card. Captured on the window rather
  // than the card so a stray focus outside it still cannot escape the trap —
  // and registered before the page's own Escape handler can close the map
  // underneath us, which would leave the dialog orphaned over the globe.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        choose('dismissed');
        return;
      }
      if (event.key !== 'Tab') return;

      const card = cardRef.current;
      if (!card) return;
      const items = Array.from(card.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      // Also covers focus having drifted outside the card entirely (a click on
      // the scrim, an extension stealing focus): anything not in `items` wraps
      // back to an end of the list rather than continuing into the page.
      if (event.shiftKey && (active === first || !card.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !card.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [choose]);

  // Click-outside. `pointerdown` rather than `click` so a press that starts on
  // the scrim cannot finish as a click on a map province underneath.
  const onScrimPointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (cardRef.current?.contains(event.target as Node)) return;
    event.preventDefault();
    choose('dismissed');
  };

  return (
    <motion.div
      // Above the mobile takeover (z-50) and the page's floating "Go home" pill
      // (z-[110]): while this is open it is the only thing that may be clicked,
      // which is also what keeps a second selection from racing the first.
      className="fixed inset-0 z-[120] bg-gray-950/45 backdrop-blur-[2px]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={FADE}
      onPointerDown={onScrimPointerDown}
    >
      <div
        className={
          isSheet
            ? 'absolute inset-x-0 bottom-0 p-3'
            : // Inset by the region panel's own width so the card sits over the
              // map column, not over the panel.
              'absolute inset-0 flex items-center justify-center p-6 md:right-80 lg:right-96'
        }
      >
        <motion.div
          ref={cardRef}
          role="dialog"
          aria-modal="true"
          // `aria-label`, not `aria-labelledby` on the heading: the visible
          // title is a greeting ("Looks like you're in Québec 👋"), which is a
          // poor name for a dialog announced out of context. The dedicated
          // string states the decision being asked for; the heading is still
          // read as content.
          aria-label={fill(content.subregionPromptAriaLabel)}
          aria-describedby={bodyId}
          className={`relative w-full ${isSheet ? 'max-w-none rounded-3xl' : 'max-w-sm rounded-2xl'}
            overflow-hidden border border-black/5 bg-white shadow-2xl
            dark:border-white/10 dark:bg-gray-900`}
          initial={
            reducedMotion
              ? { opacity: 0 }
              : isSheet
                ? { opacity: 0, y: 40 }
                : { opacity: 0, y: 10, scale: 0.94 }
          }
          animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
          exit={
            reducedMotion
              ? { opacity: 0 }
              : isSheet
                ? { opacity: 0, y: 24 }
                : { opacity: 0, y: 6, scale: 0.97 }
          }
          transition={reducedMotion ? FADE : isSheet ? SHEET_SPRING : CARD_SPRING}
        >
          {/* A wash of brand colour behind the flag, so the card reads as a
              greeting rather than as a consent box. Purely decorative. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-28
              bg-gradient-to-b from-[#00a798]/15 to-transparent dark:from-[#00a798]/20"
          />

          <button
            type="button"
            onClick={() => choose('dismissed')}
            aria-label={content.subregionPromptDismiss}
            className="absolute right-2.5 top-2.5 z-10 rounded-full p-1.5 text-gray-400
              transition-colors hover:bg-black/5 hover:text-gray-700
              focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
              focus-visible:outline-[#00a798]
              dark:hover:bg-white/10 dark:hover:text-gray-200"
          >
            <FiX className="h-4 w-4" />
          </button>

          <div className={`relative ${isSheet ? 'px-5 pb-5 pt-6' : 'px-6 pb-6 pt-7'}`}>
            <div className="flex items-start gap-3.5">
              {feature.flagSrc && (
                <motion.span
                  // The little wave. It is the one moment of personality in the
                  // whole flow, so it is a spring, not a fade — and it is the
                  // first thing reduced-motion drops. WINK ONLY: a wave on all
                  // ~500 provinces would make the greeting a mannerism.
                  className="mt-0.5 flex-none"
                  initial={
                    reducedMotion
                      ? false
                      : isWink
                        ? { rotate: -14, scale: 0.7, opacity: 0 }
                        : { opacity: 0 }
                  }
                  animate={{ rotate: 0, scale: 1, opacity: 1 }}
                  transition={
                    reducedMotion
                      ? { duration: 0 }
                      : isWink
                        ? { type: 'spring', stiffness: 500, damping: 14, delay: 0.06 }
                        : FADE
                  }
                >
                  <Image
                    src={feature.flagSrc}
                    alt=""
                    width={40}
                    height={30}
                    // Sized to the asset it is actually showing, so nothing is
                    // cropped: Québec's Fleurdelisé is 3:2, and the country
                    // flags in public/flags are all 4:3. In rem because
                    // globals.css steps the root font up by viewport, and a px
                    // flag would shrink against its own heading on a 4K screen.
                    className={`${isWink ? 'h-[1.5rem] w-[2.25rem]' : 'h-[1.5rem] w-[2rem]'}
                      rounded-[0.2rem] object-cover shadow-sm
                      ring-1 ring-black/15 dark:ring-white/25`}
                    unoptimized
                  />
                </motion.span>
              )}

              <div className="min-w-0 flex-1 pr-6">
                <p
                  className="text-[0.65rem] font-semibold uppercase tracking-[0.12em]
                    text-[#00a798]"
                >
                  {copy.eyebrow}
                </p>
                <h2
                  id={titleId}
                  className="mt-1 text-base font-bold leading-snug text-gray-900 dark:text-gray-50"
                >
                  {fill(copy.title)}
                </h2>
              </div>
            </div>

            <p
              id={bodyId}
              className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-300"
            >
              {fill(copy.body)}
            </p>

            {/* Stacked, not side by side: the labels are full sentences in
                French and would truncate in a row at 375px. */}
            <div className="mt-4 flex flex-col gap-2">
              <button
                ref={primaryRef}
                type="button"
                onClick={() => choose(copy.primary.choice)}
                className="w-full rounded-xl bg-[#00a798] px-4 py-2.5 text-sm font-semibold
                  text-white shadow-sm transition-all hover:bg-[#009286] active:scale-[0.99]
                  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                  focus-visible:outline-[#00a798]"
              >
                {fill(copy.primary.label)}
              </button>
              <button
                type="button"
                onClick={() => choose(copy.secondary.choice)}
                className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm
                  font-semibold text-gray-700 transition-colors hover:bg-gray-50
                  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                  focus-visible:outline-[#00a798]
                  dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                {fill(copy.secondary.label)}
              </button>
            </div>

            <p className="mt-3 text-center text-[0.7rem] text-gray-400 dark:text-gray-500">
              {content.subregionPromptFootnote}
            </p>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
